import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router"
import { Company } from "src/app/models/company.model"
import { Logsheet } from "src/app/models/logsheet.model"
import { Plant } from "src/app/models/plant.model"
import { Unit } from "src/app/models/unit.model"
import { CompanyService } from "src/app/services/company/company.service"
import { DataPointService } from "src/app/services/data-point/data-point.service"
import { LogsheetService } from "src/app/services/logsheet/logsheet.service"
import { PlantService } from "src/app/services/plant/plant.service"
import { ToastService } from "src/app/services/toast/toast.service"
import { UnitService } from "src/app/services/unit/unit.service"
import { OverviewService } from 'src/app/services/overview/overview.service';
import { ErrorHandlerService } from 'src/app/services/errorHandler/error-handler.service';
import { Roles, isAdmin, isSuperAdmin } from "src/app/auth/roles";
import { AvailableWorkflow, WorkflowRuntimeListItem, WorkflowService } from 'src/app/services/workflow/workflow.service';
import { RefreshStateService } from 'src/app/services/refresh-state/refresh-state.service';
import { Subscription } from 'rxjs';
import {
	StageSegment,
	StatusToken,
	overdueLabel,
	stageProgressLabel,
	stageSegments,
	statusText,
	statusToken
} from 'src/app/components/workflow-card/workflow-card.helpers';

interface HomeRefreshState {
	activeTab: 'workflows' | 'forms'
	workflowFilter: 'tasks' | 'start' | 'history'
	historySearch: string
	historyStatusFilter: 'all' | 'inprogress' | 'completed' | 'rejected'
	workflowSort: 'newest' | 'oldest'
	workflowPage: number
	adminWfPage: number
	wfSelectedCompany: any
	wfSelectedPlant: any
	selectedValues: any
	currentPage: number
}

@Component({
	standalone: false,
	selector: 'app-home',
	templateUrl: './home.component.html',
	styleUrls: ['../../components/workflow-card/workflow-card.css', './home.component.css']
})
export class HomeComponent implements OnDestroy {
	private readonly refreshStateKey = 'home'
	private readonly menuResetSubscription = new Subscription()
	activeTab: 'workflows' | 'forms' = 'workflows'
	needsMyAction: WorkflowRuntimeListItem[] = []
	availableToStart: AvailableWorkflow[] = []
	workflowsLoading = false
	workflowError = ''
	showHistory = false
	workflowFilter: 'tasks' | 'start' | 'history' = 'tasks'
	historyItems: WorkflowRuntimeListItem[] = []
	historyLoading = false
	historyError = ''
	plantDetails: Plant = {
		plantId: 0,
		plantName: "",
		isDeleted: false,
	}
	showDeactivateMenu = false
	showMenu = false
	showOverview = false;
	activeItem = 0
	$: any
	toggleMenu(plantId: number) {
		this.activeItem = plantId
		this.showMenu = !this.showMenu
	}
	toggleOverview() {
		this.overviewService.toggleOverview();
	}
	selectedLogsheet: number = 0;
	companies: Company[] = []
	plants: Plant[] = []
	units: Unit[] = []
	logsheets: Logsheet[] = []
	currentRole: any = ""
	roles = Roles
	assignedPlants: any = []
	firstRender: boolean = history?.state?.firstRender ?? false
	condition: boolean = this.currentRole !== Roles.superAdmin || this.currentRole !== '' ? true : false;
	plantId = 0
	currentPage: number = 1
	pageSize = 9
	totalPages = 10
	loopArray = new Array(this.pageSize).fill(0).map((_, index) => index + 1)
	currentIncrement = this.pageSize
	selectedPage = {
		backgroundColor: "#0F2642",
		color: "white",
		borderRadius: "4px",
	}
	selectedValues = history?.state?.selectedValues ?? {
		company: {
			companyId: 0,
			companyName: "",
			isDeleted: false,
		},
		plant: {
			plantId: 0,
			plantName: "",
			isDeleted: false,
		}
	}
	errorMessage = {
		success: true,
		message: ''
	}

	constructor(
		private route: ActivatedRoute,
		private plantService: PlantService,
		private companyService: CompanyService,
		private dataPointService: DataPointService,
		private unitService: UnitService,
		private router: Router,
		public toastService: ToastService,
		public logsheetService: LogsheetService,
		public overviewService: OverviewService,
		private errorService: ErrorHandlerService,
		private workflowService: WorkflowService,
		private refreshState: RefreshStateService
	) { }

	setActiveTab(tab: 'workflows' | 'forms') {
		this.activeTab = tab
		if (tab === 'workflows' && this.needsMyAction.length === 0 && this.availableToStart.length === 0) {
			this.loadMyWork()
		}
		this.persistRefreshState()
	}

	loadMyWork() {
		this.workflowsLoading = true
		this.workflowError = ''
		this.workflowService.getMyWork().subscribe({
			next: (response) => {
				this.needsMyAction = (response.needsMyAction || []).filter(item => !this.isTerminalWorkflowItem(item))
				this.availableToStart = response.availableToStart || []
				this.workflowPage = Math.min(this.workflowPage, this.totalCardPages(this.activeWorkflowCount))
				this.workflowsLoading = false
			},
			error: (err) => {
				this.workflowsLoading = false
				this.workflowError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load workflows'
			}
		})
	}

	loadAvailableWorkflows() {
		this.loadMyWork()
	}

	loadHistory() {
		this.showHistory = true
		this.historyLoading = true
		this.historyError = ''
		this.workflowService.getWorkflowHistory().subscribe({
			next: (items) => {
				this.historyItems = items || []
				this.historyLoading = false
			},
			error: (err) => {
				this.historyLoading = false
				this.historyError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load workflow history'
			}
		})
	}

	hideHistory() {
		this.showHistory = false
	}

	/** Switches the workflow view (My Tasks / Start New / History) via the filter tabs. */
	setWorkflowFilter(filter: 'tasks' | 'start' | 'history') {
		this.workflowFilter = filter
		this.workflowPage = 1
		if (filter === 'history') {
			this.loadHistory()
		} else {
			this.showHistory = false
		}
		this.persistRefreshState()
	}

	/* ---- Client-side pagination for the workflow card lists ---- */
	cardPageSize = 9
	workflowPage = 1
	adminWfPage = 1

	private paginate<T>(items: T[], page: number): T[] {
		const start = (page - 1) * this.cardPageSize
		return items.slice(start, start + this.cardPageSize)
	}

	totalCardPages(count: number): number {
		return Math.max(1, Math.ceil((count || 0) / this.cardPageSize))
	}

	cardPageList(count: number): number[] {
		return Array.from({ length: this.totalCardPages(count) }, (_, i) => i + 1)
	}

	/** Item count for the currently visible workflow tab, used to size pagination. */
	get activeWorkflowCount(): number {
		if (this.workflowFilter === 'tasks') return this.needsMyAction.length
		if (this.workflowFilter === 'start') return this.availableToStart.length
		return this.filteredHistoryItems().length
	}

	setWorkflowPage(page: number) {
		this.workflowPage = Math.min(Math.max(1, page), this.totalCardPages(this.activeWorkflowCount))
		this.persistRefreshState()
	}

	get pagedNeedsMyAction(): WorkflowRuntimeListItem[] {
		// #3: My Tasks honours the same submission-date ordering as History (newest first by default).
		return this.paginate(this.sortBySubmission(this.needsMyAction), this.workflowPage)
	}

	get pagedAvailableToStart(): AvailableWorkflow[] {
		return this.paginate(this.availableToStart, this.workflowPage)
	}

	setAdminWfPage(page: number) {
		this.adminWfPage = Math.min(Math.max(1, page), this.totalCardPages(this.adminWorkflows.length))
		this.persistRefreshState()
	}

	get pagedAdminWorkflows(): WorkflowRuntimeListItem[] {
		return this.paginate(this.adminWorkflows, this.adminWfPage)
	}

	/* ---- History search (debounced) + status filter ---- */
	historySearch = ''
	historyStatusFilter: 'all' | 'inprogress' | 'completed' | 'rejected' = 'all'
	historyFilterOpen = false
	historyStatusOptions: { value: 'all' | 'inprogress' | 'completed' | 'rejected'; label: string }[] = [
		{ value: 'all', label: 'All statuses' },
		{ value: 'inprogress', label: 'In Progress' },
		{ value: 'completed', label: 'Completed' },
		{ value: 'rejected', label: 'Rejected' },
	]
	private historySearchTimer: any = null

	/** Debounce the history search so we only re-filter ~300ms after typing stops. */
	onHistorySearch(value: string) {
		if (this.historySearchTimer) clearTimeout(this.historySearchTimer)
		this.historySearchTimer = setTimeout(() => {
			this.historySearch = (value || '').toLowerCase().trim()
			this.workflowPage = 1
			this.persistRefreshState()
		}, 300)
	}

	toggleHistoryFilter() {
		this.historyFilterOpen = !this.historyFilterOpen
	}

	setHistoryStatusFilter(filter: 'all' | 'inprogress' | 'completed' | 'rejected') {
		this.historyStatusFilter = filter
		this.historyFilterOpen = false
		this.workflowPage = 1
		this.persistRefreshState()
	}

	/** Status bucket key for an item, used by both grouping and the status filter. */
	private historyBucket(item: WorkflowRuntimeListItem): 'inprogress' | 'completed' | 'rejected' {
		const status = (item.status || '').toString().trim().toUpperCase()
		return status === 'COMPLETED' ? 'completed' : status === 'REJECTED' ? 'rejected' : 'inprogress'
	}

	/** Whether an item matches the current debounced search query. */
	private matchesHistorySearch(item: WorkflowRuntimeListItem): boolean {
		if (!this.historySearch) return true
		const haystack = [
			item.name,
			item.summaryTitle,
			'ref #' + item.workflowItemId,
			...(item.summaryFields || []).map((f) => f.value)
		].join(' ').toLowerCase()
		return haystack.includes(this.historySearch)
	}

	/* ---- #3: sort submitted-form cards by submission date (newest/oldest) ---- */
	workflowSort: 'newest' | 'oldest' = 'newest'

	toggleWorkflowSort() {
		this.workflowSort = this.workflowSort === 'newest' ? 'oldest' : 'newest'
		this.workflowPage = 1
		this.persistRefreshState()
	}

	/** Order a list of workflow items by submission date per the active sort direction. */
	private sortBySubmission(items: WorkflowRuntimeListItem[]): WorkflowRuntimeListItem[] {
		const dir = this.workflowSort === 'newest' ? -1 : 1
		return [...items].sort((a, b) => {
			const ta = new Date(a.startedAt || 0).getTime()
			const tb = new Date(b.startedAt || 0).getTime()
			return (ta - tb) * dir
		})
	}

	/** History items narrowed by the active search query and status filter (flat, ungrouped). */
	private filteredHistoryItems(): WorkflowRuntimeListItem[] {
		const filtered = this.historyItems.filter((item) => {
			const key = this.historyBucket(item)
			if (this.historyStatusFilter !== 'all' && this.historyStatusFilter !== key) return false
			return this.matchesHistorySearch(item)
		})
		return this.sortBySubmission(filtered)
	}

	/** Current page of filtered history as a single flat list (no status grouping). */
	get pagedHistoryItems(): WorkflowRuntimeListItem[] {
		return this.paginate(this.filteredHistoryItems(), this.workflowPage)
	}

	/* ---- Super Admin: browse all workflows by account holder + client ---- */
	wfSelectedCompany: any = { companyId: 0, companyName: '' }
	wfSelectedPlant: any = { plantId: 0, plantName: '' }
	wfPlants: Plant[] = []
	adminWorkflows: WorkflowRuntimeListItem[] = []
	adminWorkflowsLoading = false
	adminWorkflowsError = ''

	onWfCompanySelect(company: any) {
		this.wfSelectedCompany = company
		this.wfSelectedPlant = { plantId: 0, plantName: '' }
		this.adminWfPage = 1
		this.wfPlants = []
		this.adminWorkflows = []
		this.adminWorkflowsError = ''
		this.persistRefreshState()
		if (!company?.companyId) return
		this.plantService.retrieveCompanyPlants(String(company.companyId)).subscribe({
			next: (plants) => (this.wfPlants = plants),
			error: (err) => (this.adminWorkflowsError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load clients')
		})
	}

	onWfPlantSelect(plant: any) {
		this.wfSelectedPlant = plant
		this.adminWorkflows = []
		this.adminWfPage = 1
		this.persistRefreshState()
		if (!this.wfSelectedCompany?.companyId || !plant?.plantId) return
		this.loadSelectedAdminWorkflows()
	}

	private loadSelectedAdminWorkflows() {
		this.adminWorkflowsLoading = true
		this.adminWorkflowsError = ''
		this.workflowService.getWorkflowsForScope(this.wfSelectedCompany.companyId, this.wfSelectedPlant.plantId).subscribe({
			next: (items) => {
				this.adminWorkflows = items || []
				this.adminWfPage = Math.min(this.adminWfPage, this.totalCardPages(this.adminWorkflows.length))
				this.adminWorkflowsLoading = false
			},
			error: (err) => {
				this.adminWorkflowsLoading = false
				const base = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load workflows'
				this.adminWorkflowsError = `${base} (HTTP ${err?.status ?? '?'})`
			}
		})
	}

	openWorkflow(workflow: AvailableWorkflow) {
		this.router.navigate(['/workflow-runtime', 'definitions', workflow.workflowVersionId])
	}

	openWorkflowItem(item: WorkflowRuntimeListItem, fromHistory = false) {
		// Activity history is only surfaced when an item is opened from the History section,
		// not when acting on a live stage.
		if (fromHistory) {
			this.router.navigate(['/workflow-runtime', 'instances', item.workflowItemId], { queryParams: { view: 'history' } })
			return
		}
		this.router.navigate(['/workflow-runtime', 'instances', item.workflowItemId])
	}

	/* ---- Inline card actions (approve one-click; return/reject with a reason panel) ---- */
	reasonForItemId: number | null = null
	reasonMode: 'return' | 'reject' | null = null
	reasonText = ''
	actionBusy = false

	startReason(item: WorkflowRuntimeListItem, mode: 'return' | 'reject') {
		this.reasonForItemId = item.workflowItemId
		this.reasonMode = mode
		this.reasonText = ''
	}

	cancelReason() {
		this.reasonForItemId = null
		this.reasonMode = null
		this.reasonText = ''
	}

	confirmReason(item: WorkflowRuntimeListItem) {
		if (this.actionBusy) return
		this.actionBusy = true
		const req = { reason: this.reasonText?.trim() || undefined }
		const call = this.reasonMode === 'reject'
			? this.workflowService.rejectWorkflow(item.workflowItemId, req)
			: this.workflowService.returnWorkflow(item.workflowItemId, req)
		call.subscribe({
			next: () => { this.actionBusy = false; this.cancelReason(); this.loadMyWork() },
			error: (err) => {
				this.actionBusy = false
				this.workflowError = this.errorService.handleRetrievalErrors(err)?.message || 'Action failed'
			}
		})
	}

	approveInline(item: WorkflowRuntimeListItem) {
		if (this.actionBusy) return
		this.actionBusy = true
		this.workflowService.approveWorkflow(item.workflowItemId).subscribe({
			next: () => { this.actionBusy = false; this.loadMyWork() },
			error: (err) => {
				this.actionBusy = false
				this.workflowError = this.errorService.handleRetrievalErrors(err)?.message || 'Approval failed'
			}
		})
	}

	/** Approval chain steps with done/active/pending state for the card. */
	chainSteps(item: WorkflowRuntimeListItem): { label: string; state: 'done' | 'active' | 'pending'; you: boolean }[] {
		const n = item.stageCount || 0
		if (n <= 0) return []
		const current = item.currentStageNumber || 1
		const steps: { label: string; state: 'done' | 'active' | 'pending'; you: boolean }[] = []
		for (let s = 1; s <= n; s++) {
			const label = s === 1 ? 'Submitter' : `Approver ${s - 1}`
			const state: 'done' | 'active' | 'pending' = s < current ? 'done' : s === current ? 'active' : 'pending'
			steps.push({ label, state, you: s === current && !!item.canAct })
		}
		return steps
	}

	statusLabel(item: WorkflowRuntimeListItem) {
		return item.derivedStatusLabel || item.statusLabel || item.status
	}

	rowActionLabel(item: WorkflowRuntimeListItem) {
		if (this.isTerminalWorkflowItem(item)) return item.actionLabel || 'View Status'
		if (item.canResubmit) return item.actionLabel || 'Continue'
		if (item.canApprove || item.canReturn || item.canReject || item.canAct) return item.actionLabel || 'Take Action'
		return item.actionLabel || 'View Status'
	}

	isTerminalWorkflowItem(item: WorkflowRuntimeListItem) {
		const status = (item.status || '').toString().trim().toUpperCase()
		return status === 'COMPLETED' || status === 'REJECTED'
	}

	formatWorkflowDate(value?: string | null) {
		return value ? new Date(value).toLocaleString() : ''
	}

	/** Compact "time ago" for the card footer (replaces the noisy full timestamp). */
	/** #N1: the backend stores UTC; a timezone-less timestamp must be parsed as UTC, not local,
	 *  otherwise every recent item reads as "Just now" (local parse lands ~tz-offset in the future). */
	private asUtcDate(value: string): Date {
		const normalized = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : value + 'Z'
		return new Date(normalized)
	}

	relativeTime(value?: string | null): string {
		if (!value) return ''
		const then = this.asUtcDate(value).getTime()
		if (isNaN(then)) return ''
		const mins = Math.floor((Date.now() - then) / 60000)
		if (mins < 1) return 'Just now'
		if (mins < 60) return `${mins}m ago`
		const hrs = Math.floor(mins / 60)
		if (hrs < 24) return `${hrs}h ago`
		const days = Math.floor(hrs / 24)
		if (days < 7) return `${days}d ago`
		return new Date(value).toLocaleDateString()
	}

	/** Specific, human label for the status chip (Returned / Needs approval / Awaiting / …). */
	pillLabel(item: WorkflowRuntimeListItem): string {
		return statusText(item)
	}

	/** The single primary status this card resolves to, from a closed vocabulary. */
	statusToken(item: WorkflowRuntimeListItem): StatusToken {
		return statusToken(item)
	}

	/** Overdue is a secondary condition on the current stage; empty string means on time. */
	overdueLabel(item: WorkflowRuntimeListItem): string {
		return overdueLabel(item)
	}

	stageSegments(item: WorkflowRuntimeListItem): StageSegment[] {
		return stageSegments(item)
	}

	stageProgressLabel(item: WorkflowRuntimeListItem): string {
		return stageProgressLabel(item)
	}

	/** Workflow-specific summary values, capped so the facts grid can't grow unbounded. */
	summaryMetaFields(item: WorkflowRuntimeListItem): { label: string; value: string }[] {
		return (item.summaryFields || []).slice(0, 3)
	}

	/** One utterance for screen readers instead of a dozen disconnected fragments. */
	cardAriaLabel(item: WorkflowRuntimeListItem): string {
		const overdue = this.overdueLabel(item)
		return [
			item.name,
			item.summaryTitle || '',
			this.pillLabel(item),
			this.stageProgressLabel(item),
			overdue
		]
			.filter(Boolean)
			.join(', ')
	}

	/** Whether this item is the initiator's to act on after a return. */
	isReturned(item: WorkflowRuntimeListItem): boolean {
		return (item.lastAction || '').toString().trim().toUpperCase() === 'RETURNED'
	}

	/** The approval path a workflow routes through, derived from its stage count. */
	stageFlow(count?: number): string[] {
		const n = count || 0
		if (n <= 0) return []
		const labels = ['You submit']
		for (let i = 2; i <= n; i++) labels.push(`Approver ${i - 1}`)
		return labels
	}

	/** Short, human date e.g. "15 Jun 2026" for the submitted line. */
	formatShort(value?: string | null): string {
		if (!value) return ''
		const d = this.asUtcDate(value)
		if (isNaN(d.getTime())) return ''
		return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
	}

	/** Two-letter initials for the submitter avatar. */
	initials(name?: string | null): string {
		const parts = (name || '').trim().split(/\s+/).filter(Boolean)
		if (parts.length === 0) return '—'
		if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
		return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
	}

	retrieveLogsheetsByUnitId(unit: any) {
		for (let i = 0; i < unit.length; i++) {
			this.logsheetService
				.retrieveLogsheetByUnit(unit[i].unitId)
				.subscribe({
					next: (response) => {
						unit[i].logsheets = response;
						unit[i].logsheets = unit[i].logsheets.filter((f: any)=> f.displayStatus != 'deactivated')

					},
					error: (err) => {
						this.errorMessage = this.errorService.handleRetrievalErrors(err)
					},
				})
		}

	}

	retrieveUnitsByPlantId(selected: any) {
		if (!this.firstRender) {
			this.selectedValues = {
				...this.selectedValues,
				plant: selected,
			}

			this.selectedValues.unit = {
				unitId: 0,
				unitName: "",
				isDeleted: false,
			}
			this.logsheets = []
		}
		this.persistRefreshState()
		this.retrieveUnits(selected?.plantId)
	}

	showPlantEmptyState() {
		if (
			isSuperAdmin(this.currentRole) &&
			this.selectedValues?.company?.companyId > 0 &&
			(this.plants?.length === 0 ||
				this.selectedValues?.plant?.plantId === 0) &&
			(this.selectedValues?.unit?.unitId ?? 0) === 0
		) {
			return true
		} else if (
			isAdmin(this.currentRole) &&
			(this.plants?.length === 0 ||
				this.selectedValues?.plant?.plantId === 0) &&
			(this.selectedValues?.unit?.unitId ?? 0) === 0
		) {
			return true
		} else {
			return false
		}
	}

	retrievePlantsByCompanyId(selected: any) {
		if (!this.firstRender) {
			this.selectedValues = {
				...this.selectedValues,
				company: selected,
			}

			this.selectedValues.plant = {
				plantId: 0,
				plantName: "",
				isDeleted: false,
			}
			this.selectedValues.unit = {
				unitId: 0,
				unitName: "",
				isDeleted: false,
			}
			this.units = []
		}
		this.persistRefreshState()
		this.plantService
			.retrieveCompanyPlants(
				this.selectedValues?.company?.companyId.toString(),
			)
			.subscribe({
				next: (response) => {
					this.plants = response
				},
				error: (err) => {
					this.errorMessage = this.errorService.handleRetrievalErrors(err)
				},
			})

	}

	private restoreRefreshState(): void {
		const state = this.refreshState.load<HomeRefreshState>(this.refreshStateKey)
		if (!state) return

		if (state.activeTab === 'forms' || state.activeTab === 'workflows') this.activeTab = state.activeTab
		if (['tasks', 'start', 'history'].includes(state.workflowFilter)) this.workflowFilter = state.workflowFilter
		if (['all', 'inprogress', 'completed', 'rejected'].includes(state.historyStatusFilter)) {
			this.historyStatusFilter = state.historyStatusFilter
		}
		if (state.workflowSort === 'newest' || state.workflowSort === 'oldest') this.workflowSort = state.workflowSort
		this.historySearch = state.historySearch || ''
		this.workflowPage = Math.max(1, Number(state.workflowPage) || 1)
		this.adminWfPage = Math.max(1, Number(state.adminWfPage) || 1)
		this.currentPage = Math.max(1, Number(state.currentPage) || 1)
		if (state.wfSelectedCompany?.companyId) this.wfSelectedCompany = state.wfSelectedCompany
		if (state.wfSelectedPlant?.plantId) this.wfSelectedPlant = state.wfSelectedPlant
		if (state.selectedValues?.company && state.selectedValues?.plant) {
			this.selectedValues = state.selectedValues
		}
	}

	private persistRefreshState(): void {
		this.refreshState.save<HomeRefreshState>(this.refreshStateKey, {
			activeTab: this.activeTab,
			workflowFilter: this.workflowFilter,
			historySearch: this.historySearch,
			historyStatusFilter: this.historyStatusFilter,
			workflowSort: this.workflowSort,
			workflowPage: this.workflowPage,
			adminWfPage: this.adminWfPage,
			wfSelectedCompany: this.wfSelectedCompany,
			wfSelectedPlant: this.wfSelectedPlant,
			selectedValues: this.selectedValues,
			currentPage: this.currentPage,
		})
	}

	private restoreWorkflowBrowse(companies: Company[]): void {
		if (!this.wfSelectedCompany?.companyId) return
		const company = companies.find((item) => item.companyId === this.wfSelectedCompany.companyId)
		if (!company) {
			this.wfSelectedCompany = { companyId: 0, companyName: '' }
			this.wfSelectedPlant = { plantId: 0, plantName: '' }
			this.persistRefreshState()
			return
		}

		const restoredPlantId = this.wfSelectedPlant?.plantId
		this.wfSelectedCompany = company
		this.plantService.retrieveCompanyPlants(String(company.companyId)).subscribe({
			next: (plants) => {
				this.wfPlants = plants
				const plant = plants.find((item) => item.plantId === restoredPlantId)
				if (!plant) {
					this.wfSelectedPlant = { plantId: 0, plantName: '' }
					this.persistRefreshState()
					return
				}
				this.wfSelectedPlant = plant
				this.loadSelectedAdminWorkflows()
			},
			error: (err) => {
				this.adminWorkflowsError = this.errorService.handleRetrievalErrors(err)?.message || 'Unable to load clients'
			}
		})
	}

	private restoreFormsScope(): void {
		const plantId = this.selectedValues?.plant?.plantId
		if (!plantId) return

		if (isAdmin(this.currentRole)) {
			const plant = this.plants.find((item) => item.plantId === plantId)
			if (plant) {
				this.selectedValues = { ...this.selectedValues, plant }
				this.retrieveUnits(plant.plantId, true)
			}
			return
		}

		const companyId = this.selectedValues?.company?.companyId
		if (!companyId) return
		this.plantService.retrieveCompanyPlants(String(companyId)).subscribe({
			next: (plants) => {
				this.plants = plants
				const plant = plants.find((item) => item.plantId === plantId)
				if (plant) {
					this.selectedValues = { ...this.selectedValues, plant }
					this.retrieveUnits(plant.plantId, true)
				}
			},
			error: (err) => {
				this.errorMessage = this.errorService.handleRetrievalErrors(err)
			}
		})
	}

	private resetToMenuHead(): void {
		if (this.historySearchTimer) clearTimeout(this.historySearchTimer)
		this.activeTab = 'workflows'
		this.workflowFilter = 'tasks'
		this.workflowSort = 'newest'
		this.workflowPage = 1
		this.adminWfPage = 1
		this.currentPage = 1
		this.historySearch = ''
		this.historyStatusFilter = 'all'
		this.historyFilterOpen = false
		this.showHistory = false
		this.historyItems = []
		this.historyError = ''
		this.wfSelectedCompany = { companyId: 0, companyName: '' }
		this.wfSelectedPlant = { plantId: 0, plantName: '' }
		this.wfPlants = []
		this.adminWorkflows = []
		this.adminWorkflowsError = ''
		this.selectedValues = {
			company: { companyId: 0, companyName: '', isDeleted: false },
			plant: { plantId: 0, plantName: '', isDeleted: false },
			unit: { unitId: 0, unitName: '', isDeleted: false },
		}
		this.plants = isAdmin(this.currentRole) ? [...this.assignedPlants] : []
		this.units = []
		this.logsheets = []
		this.selectedLogsheet = 0
		this.reasonForItemId = null
		this.reasonMode = null
		this.reasonText = ''
		this.showMenu = false
		this.showOverview = false
		this.firstRender = false
		this.overviewService.resetToHome()
		this.loadMyWork()
	}

	ngOnInit(): void {
		this.currentRole = localStorage.getItem("role")
		this.menuResetSubscription.add(
			this.refreshState.areaReset$.subscribe(area => {
				if (area === 'home') this.resetToMenuHead()
			}),
		)
		this.firstRender = history.state.firstRender ?? false
		this.restoreRefreshState()
		if (this.router.url.startsWith('/home/overview')) {
			this.overviewService.showOverview()
		}
		this.showOverview = false
		this.loadMyWork()
		if (this.workflowFilter === 'history' && !isSuperAdmin(this.currentRole)) this.loadHistory()
			this.assignedPlants = []
			try {
				const storedAssignment = localStorage.getItem("assignedPlants")
				if (storedAssignment) {
					const parsedAssignment = JSON.parse(storedAssignment)
					this.assignedPlants = Array.isArray(parsedAssignment)
						? parsedAssignment
						: []
				} 
				else {
					console.error("No data found in local storage for key 'assignedPlants' ");
				}
		} catch (error) {
			console.error("Error parsing JSON data from local storage:", error);
		}
		if (this.firstRender == true) {
			this.selectedValues = { ...history?.state?.selectedValues }
			this.retrievePlantsByCompanyId(this.selectedValues?.company)
			this.retrieveUnitsByPlantId(this.selectedValues?.plant)
			this.firstRender = false
		}

		if (isAdmin(this.currentRole)) {
			this.assignedPlants.forEach((assignment: Plant) => this.plants.push(assignment))
			this.restoreFormsScope()
			// Admin has no company picker — default-select their first assigned client so forms load automatically
			if (!this.firstRender && this.plants.length > 0 && !this.selectedValues?.plant?.plantId) {
				this.retrieveUnitsByPlantId(this.plants[0])
			}
		} else {
			this.companyService.retrieveCompanies().subscribe({
				next: (companies) => {
					this.companies = companies
					this.restoreWorkflowBrowse(companies)
					this.restoreFormsScope()
				},
				error: (response) => {
					this.errorMessage = this.errorService.handleRetrievalErrors(response)
				},
			})
		}
	}

	ngOnDestroy(): void {
		if (this.historySearchTimer) clearTimeout(this.historySearchTimer)
		this.menuResetSubscription.unsubscribe()
		this.persistRefreshState()
	}

	retrieveUnits(plantId: any, reset?: any) {
		if (reset !== true) {
			this.currentPage = 1
		}
		const pageNumber = this.currentPage
		const pageSize = this.pageSize

		const item = {
			plantId,
			pageSize,
			pageNumber
		}

		this.unitService.retrieveUnitPlantByPage(item).subscribe({
			next: (response: any) => {
				this.units = response?.item1
				this.retrieveLogsheetsByUnitId(this.units);
				this.totalPages = response?.item2
				this.loopArray = new Array(this.totalPages)
					.fill(0)
					.map((_, index) => index + 1)
			},
			error: (response) => {
				this.errorMessage = this.errorService.handleRetrievalErrors(response)
			},
		})
	}

	incrementArray(arr: any, increment: any) {
		return arr.map((item: any) => item + increment)
	}

	decrementArray(arr: any, decrement: any) {
		return arr.map((item: any) => item - decrement)
	}

	shiftPageRight() {
		const incrementedArray = this.incrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = incrementedArray
	}
	shiftPageLeft() {
		const decrementArray = this.decrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = decrementArray
	}
	setCurrentPage(page: any) {
		this.currentPage = page
		this.persistRefreshState()
		this.retrieveUnits(this.selectedValues?.plant.plantId, true)
	}
	performIncrement() {
		if (this.currentPage === this.totalPages) return
		const result = this.currentPage + 1
		this.setCurrentPage(result)
		if (this.currentPage > this.loopArray[this.loopArray.length - 1]) {
			this.shiftPageRight()
		}
	}

	performDecrement() {
		if (this.currentPage === 1) return
		const result = this.currentPage - 1
		this.setCurrentPage(result)
		if (this.currentPage < this.loopArray[0]) {
			this.shiftPageLeft()
		}
	}


	emptyStateCompanies = {
		imgPath: "../../assets/company.svg",
		message1: "Select from the dropdown list(s) above to view associated Branches",
		message2: "",
	}

	emptyStatePlants = {
		imgPath: "../../assets/plant.svg",
		message1: "Select from the dropdown list(s) above to view associated Branches",
		message2: "",
	}

	emptyStateUnits = {
		imgPath: "../../assets/unit.svg",
		message1: "No branches to display",
		message2: "",
	}
}
